import { useVirtualizer } from "@tanstack/react-virtual";
import { Checkbox, Modal, Tabs, Typography, Upload } from "antd";
import { ApiService } from "api/api.service";
import { SingleQueryWrapper } from "components/QueryWrapper";
import { useRef, useState } from "react";
import { TbLoader2, TbPhoto, TbUpload } from "react-icons/tb";

export default function ImageGalleryModal({ value, onChange }: { value?: string; onChange?: (val: string) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const createMedia = ApiService.media.useCreateMedia();
  const mediaList = ApiService.media.useGetAllImages();

  return (
    <>
      <div
        className="border border-solid border-gray-200 rounded-lg aspect-[16/9] overflow-hidden cursor-pointer w-full bg-gray-100 flex flex-col gap-2 items-center justify-center"
        onClick={() => {
          setIsOpen(true);
        }}
      >
        {value ? (
          <img src={value} className="object-cover w-full" />
        ) : (
          <>
            <TbPhoto size={48} className="stroke-gray-300" />
            <Typography.Text type="secondary">Featured Image</Typography.Text>
          </>
        )}
      </div>
      <Modal
        title="Select or Upload Thumbnail"
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        footer={null}
        width={548}
      >
        <Tabs
          defaultActiveKey="Select"
          items={[
            {
              key: "Select",
              label: "Select",
              children: (
                <SingleQueryWrapper
                  query={mediaList}
                  renderData={(images) => {
                    return <VirtualGallery images={images} value={value} onChange={onChange} />;
                  }}
                />
              ),
            },
            {
              key: "Upload",
              label: "Upload",
              children: (
                <Upload.Dragger
                  name="file"
                  customRequest={(options) => {
                    const formData = new FormData();
                    formData.append("file", options.file);
                    createMedia.mutate(formData, {
                      onSuccess: (data) => {
                        onChange?.(data.data.result?.[0].url);
                        setIsOpen(false);
                      },
                    });
                  }}
                  showUploadList={false}
                >
                  <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                    <p className="ant-upload-drag-icon">
                      {createMedia.isPending ? (
                        <TbLoader2 size={28} className="animate-spin" />
                      ) : (
                        <TbUpload size={28} />
                      )}
                    </p>
                    <p className="ant-upload-text">Click or drag files to this area to upload</p>
                    <p className="ant-upload-hint">Supports single or bulk upload.</p>
                  </div>
                </Upload.Dragger>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
}

function VirtualGallery({
  images,
  value,
  onChange,
}: {
  images: string[];
  value: string | undefined;
  onChange: ((val: string) => void) | undefined;
}) {
  const parentRef = useRef(null);
  const COLUMNS = 4;
  const CELL_WIDTH = 120;
  const CELL_HEIGHT = 90;
  const TOTAL_ITEMS = images.length;
  const ROWS = Math.ceil(TOTAL_ITEMS / 5);
  const GAP = 6;

  const rowVirtualizer = useVirtualizer({
    count: ROWS,
    // count: images.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CELL_HEIGHT,
    // lanes: 5,
    gap: GAP,
    overscan: 8,
  });
  return (
    <div
      ref={parentRef}
      style={{ height: "400px", overflowY: "auto", width: COLUMNS * CELL_WIDTH + (COLUMNS - 1) * GAP }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {/* Only the visible items in the virtualizer, manually positioned to be in view */}
        {rowVirtualizer.getVirtualItems().map((virtualItem) => (
          <div
            key={virtualItem.key}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              // width: "100%",
              // height: `${virtualItem.size}px`,
              transform: `translateY(${virtualItem.start}px)`,
              display: "grid",
              gridTemplateColumns: `repeat(${COLUMNS}, ${CELL_WIDTH}px)`,
              gap: GAP,
            }}
          >
            {Array.from({ length: COLUMNS }).map((_, colIndex) => {
              const url = images[virtualItem.index * COLUMNS + colIndex];
              const isSelected = url === value;
              return (
                <div
                  key={`${virtualItem.index}-${colIndex}`}
                  style={{
                    width: CELL_WIDTH,
                    height: CELL_HEIGHT,
                    display: "flex",
                    cursor: "pointer",
                    position: "relative",
                  }}
                  onClick={() => {
                    onChange?.(url);
                  }}
                >
                  <img
                    style={{
                      width: CELL_WIDTH,
                      height: CELL_HEIGHT,
                    }}
                    className={`aspect-[3/2] object-cover rounded-md  border-3 border-solid ${isSelected ? "border-brand-500" : "border-transparent"}`}
                    src={url}
                  />
                  {isSelected ? <Checkbox checked className="absolute left-2 bottom-2" /> : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
